import { Component, Input, OnInit } from '@angular/core';
import { ModalController, AlertController, ActionSheetController, ToastController } from '@ionic/angular';
import { Trabajo } from '../../models/trabajo';
import { Metodosui } from '../../services/metodosui';
import { PlanillaCalendarComponent } from '../planilla-calendar/planilla-calendar.component';
import { CotizacioneslistComponent } from '../cotizacioneslist/cotizacioneslist.component';
import { ComentariosComponent } from '../comentarios/comentarios.component';
import { JavapiService } from '../../services/javapi.service';
import { MetodosgenService } from '../../services/metodosgen.service';
import { firstValueFrom } from 'rxjs';
import { GastoseditComponent } from '../gastosedit/gastosedit.component';
import { CotizacioneseditComponent } from '../cotizacionesedit/cotizacionesedit.component';
import { GaleriaTrabajosComponent } from '../galeria-trabajos/galeria-trabajos.component';
import { InfomodalComponent, InfoSection } from '../infomodal/infomodal.component';
import { DatePipe } from '@angular/common';
import { ArsManagementComponent } from '../ars-management/ars-management.component';
import { SeguimientoComponent } from '../seguimiento/seguimiento.component';
import { EnviarcorreoComponent } from '../enviarcorreo/enviarcorreo.component';

@Component({
  selector: 'app-trabajosmenu',
  templateUrl: './trabajosmenu.component.html',
  styleUrls: ['./trabajosmenu.component.scss'],
  standalone: false,
  providers: [DatePipe]
})
export class TrabajosmenuComponent implements OnInit {

  @Input() trabajo: Trabajo | null = null;

  // Cotización state
  cotizacionInfo: { id: number; estado: string; total: number } | null = null;
  loadingCotizacion = true;
  permissions: any = {};

  // Totals
  planillaTotalAmount: number | null = null;
  gastosTotalAmount: number | null = null;

  // Estados
  estadosTrabajos: string[] = ['No iniciado', 'En proceso', 'En pausa', 'Finalizado', 'Cancelado'];

  // Info panel
  showInfoPanel: boolean = false;
  diasTrabajados: number = 0;
  primerDiaPlanilla: string | null = null;
  cotizacionesCount: number = 0;
  galleryCount: number = 0;
  pptsAmount: number = 0;

  // ARS state
  isArsEnabled: boolean = false;
  isArsSigned: boolean = false;
  isArsUploaded: boolean = false;
  fechaFirmaArs: string | null = null;

  isUnlinking: boolean = false;

  constructor(
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private actionSheetCtrl: ActionSheetController,
    private toastCtrl: ToastController,
    private metodosUi: Metodosui,
    private javapiService: JavapiService,
    private metodosGen: MetodosgenService,
    private datePipe: DatePipe
  ) { }

  async ngOnInit() {
    this.permissions = this.metodosGen.getPermissions();
    await Promise.all([
      this.loadCotizacion(),
      this.loadMenuTotals(),
      this.checkArsActivation()
    ]);
  }

  async checkArsActivation() {
    if (!this.trabajo) return;
    try {
      // 1. Get enabled clients list from DB
      const paramRes: any = await firstValueFrom(this.javapiService.getJsonParam("enable_ars_action"));

      if (paramRes && paramRes.pa_json) {
        try {
          const enabledClients = JSON.parse(paramRes.pa_json);
          // Check if current client is in the list
          this.isArsEnabled = Array.isArray(enabledClients) && enabledClients.includes(this.trabajo.cliente);
        } catch (parseError) {
          console.error('Error parsing ARS enabled clients JSON', parseError);
          this.isArsEnabled = false;
        }
      } else {
        this.isArsEnabled = false;
      }

      // 2. If enabled, check current ARS status for this specific job
      if (this.isArsEnabled) {
        const statusPayload = this.metodosGen.setModeloGeneral(
          "genModel", "getArsInfo",
          String(this.trabajo.id),
          '', '', '', '', '', '', '', '', '', '', '', '', '', ''
        );
        const statusRes: any = await firstValueFrom(this.javapiService.getCommonModel(statusPayload));
        if (statusRes && statusRes.length > 0 && !statusRes[0].Error) {
          this.isArsSigned = statusRes[0].dt1 === 'Y';
          this.isArsUploaded = !!statusRes[0].dt3;
          this.fechaFirmaArs = statusRes[0].dt2 || null;
        } else {
          this.isArsSigned = false;
          this.isArsUploaded = false;
          this.fechaFirmaArs = null;
        }
      }
    } catch (e) {
      console.error('Error checking ARS activation', e);
      this.isArsEnabled = false;
    }
  }

  // ── Edit ────────────────────────────────
  async edit() {
    this.modalCtrl.dismiss({ action: 'edit', trabajo: this.trabajo });
  }

  // ── Delete ──────────────────────────────
  async confirmDelete() {
    this.modalCtrl.dismiss({ action: 'delete', trabajo: this.trabajo });
  }

  // ── Comments ────────────────────────────
  async openComments() {
    const modal = await this.modalCtrl.create({
      component: ComentariosComponent,
      componentProps: {
        modulo: 'TRABAJOS',
        referencia_id: this.trabajo?.id,
        titulo_referencia: `Trabajo #${this.trabajo?.id}`
      }
    });
    await modal.present();
  }

  // ── Change Status ───────────────────────
  async changeStatus() {
    const buttons = this.estadosTrabajos.map(estado => {
      let icon = 'ellipse-outline';
      let cssClass = 'action-sheet-default';
      const e = (estado || '').toLowerCase();

      if (e === 'en proceso') { icon = 'play-circle'; cssClass = 'action-sheet-blue'; }
      else if (e === 'finalizado') { icon = 'checkmark-circle'; cssClass = 'action-sheet-success'; }
      else if (e === 'en pausa') { icon = 'pause-circle'; cssClass = 'action-sheet-warning'; }
      else if (e === 'cancelado') { icon = 'close-circle'; cssClass = 'action-sheet-danger'; }
      else if (e === 'no iniciado') { icon = 'stop-circle'; cssClass = 'action-sheet-gray'; }

      return {
        text: estado,
        icon: icon,
        cssClass: cssClass,
        handler: () => {
          this.updateStatus(estado);
        }
      };
    });

    buttons.push({
      text: 'Cancelar',
      icon: 'close',
      role: 'cancel',
      cssClass: 'action-sheet-cancel',
      handler: () => { }
    } as any);

    const actionSheet = await this.actionSheetCtrl.create({
      header: 'Actualizar estado a:',
      buttons: buttons,
      cssClass: 'custom-action-sheet'
    });

    await actionSheet.present();
  }

  async updateStatus(newEstado: string) {
    if (!this.trabajo) return;

    if (newEstado === 'Finalizado') {
      if ((this.planillaTotalAmount ?? 0) <= 0) {
        const alert = await this.alertCtrl.create({
          header: 'No se puede finalizar',
          message: 'El trabajo no tiene planilla registrada. Debe agregar al menos un día de trabajo.',
          buttons: ['Entendido'],
          cssClass: 'custom-alert'
        });
        await alert.present();
        return;
      }
      if ((this.gastosTotalAmount ?? 0) <= 0) {
        const alert = await this.alertCtrl.create({
          header: 'No se puede finalizar',
          message: 'El trabajo no tiene gastos registrados. Debe registrar al menos un gasto.',
          buttons: ['Entendido'],
          cssClass: 'custom-alert'
        });
        await alert.present();
        return;
      }
      if (this.galleryCount <= 0) {
        const alert = await this.alertCtrl.create({
          header: 'No se puede finalizar',
          message: 'El trabajo no tiene imágenes en la galería. Debe subir evidencia fotográfica antes de finalizar.',
          buttons: ['Entendido'],
          cssClass: 'custom-alert'
        });
        await alert.present();
        return;
      }
    }

    try {
      const userJson = localStorage.getItem('user');
      let usuario = '';
      if (userJson) {
        const user = JSON.parse(userJson);
        usuario = user.l_usuario || '';
      }

      await firstValueFrom(
        this.javapiService.getCommonModel(
          this.metodosGen.setModeloGeneral(
            'genUpdate', 'trabajoStatusUpdate',
            String(this.trabajo.id), newEstado,
            '', usuario,
            '', '', '', '', '', '', '', '', '', '', ''
          )
        )
      );
      this.trabajo.estado = newEstado;
      await this.metodosUi.showToast(`Estado cambiado a "${newEstado}"`, 'success', 'top', 1500);
    } catch (e) {
      console.error('Error updating status:', e);
    }
  }

  async loadMenuTotals() {
    if (!this.trabajo) return;
    try {
      const data: any = await firstValueFrom(
        this.javapiService.getCommonModel(
          this.metodosGen.setModeloGeneral(
            'genModel', 'menuTotals',
            String(this.trabajo.id),
            '', '', '', '', '', '', '', '', '', '', '', '', '', ''
          )
        )
      );
      if (Array.isArray(data) && data.length > 0) {
        this.planillaTotalAmount = Number(data[0].dt1) || 0;
        this.gastosTotalAmount = Number(data[0].dt2) || 0;
        this.diasTrabajados = Number(data[0].dt3) || 0;
        this.primerDiaPlanilla = data[0].dt4 || null;
        this.cotizacionesCount = Number(data[0].dt5) || 0;
        this.galleryCount = Number(data[0].dt6) || 0;
      }
    } catch (e) {
      console.error('Error loading menu totals:', e);
    }
  }

  async loadCotizacion() {
    if (!this.trabajo) return;
    try {
      const data: any = await firstValueFrom(
        this.javapiService.getCommonModel(
          this.metodosGen.setModeloGeneral(
            'cotizaciones', 'cotizacionesT',
            String(this.trabajo.id),
            '', '', '', '', '', '', '', '', '', '', '', '', '', ''
          )
        )
      );
      if (Array.isArray(data) && data.length > 0) {
        const first = data[0];
        this.cotizacionInfo = {
          id: Number(first.dt1) || 0,
          estado: first.dt5 || 'Borrador',
          total: Number(first.dt4) || 0 // Neto sin ITBMS
        };

        try {
          const pptData: any = await firstValueFrom(
            this.javapiService.getCommonModel(
              this.metodosGen.setModeloGeneral(
                'cotizaciones', 'cotizacionGetPpt',
                String(this.cotizacionInfo.id),
                '', '', '', '', '', '', '', '', '', '', '', '', '', ''
              )
            )
          );
          if (Array.isArray(pptData) && pptData.length > 0) {
            this.pptsAmount = Number(pptData[0].dt3) || 0;
          } else {
            this.pptsAmount = 0;
          }
        } catch (e) {
          console.error('Error loading PPT:', e);
          this.pptsAmount = 0;
        }
      }
    } catch (e) {
      console.error('Error loading cotizacion:', e);
    } finally {
      this.loadingCotizacion = false;
    }
  }

  // ── Helpers ──────────────────────────
  getEstadoColor(): any {
    const e = (this.trabajo?.estado || '').toLowerCase();
    if (e === 'en proceso') return { background: 'rgba(52, 152, 219, 0.12)', color: '#2980b9' };
    if (e === 'finalizado') return { background: 'rgba(39, 174, 96, 0.12)', color: '#27ae60' };
    if (e === 'en pausa') return { background: 'rgba(243, 156, 18, 0.12)', color: '#f39c12' };
    if (e === 'cancelado') return { background: 'rgba(231, 76, 60, 0.12)', color: '#e74c3c' };
    return { background: 'rgba(149, 165, 166, 0.12)', color: '#7f8c8d' }; // No iniciado / default
  }

  async openInfo() {
    await this.metodosUi.showLoading('Cargando resumen de planilla...', 'blue');

    let diurnosTotal = 0;
    let nocturnosTotal = 0;
    const daysData: any[] = [];
    const workersMap = new Map<string, {
      nombre: string;
      diurnos: number;
      nocturnos: number;
      totalNeto: number;
      totalDays: number;
    }>();
    let dates: any[] = [];

    try {
      if (this.trabajo && this.trabajo.id) {
        dates = await firstValueFrom(
          this.javapiService.getDiasTrabajados(String(this.trabajo.id))
        ).catch(() => []);

        if (Array.isArray(dates) && dates.length > 0) {
          const user = localStorage.getItem('user_name') || 'system';
          // Carga asíncrona paralela de todas las planillas de cada fecha
          const detailsList = await Promise.all(
            dates.map(date => 
              firstValueFrom(
                this.javapiService.getPlanillaActividad(String(this.trabajo?.id), date, user)
              ).catch(() => [] as any[])
            )
          );

          // Procesar las respuestas
          for (let index = 0; index < dates.length; index++) {
            const dateStr = dates[index];
            const records = detailsList[index] || [];
            
            let dayDiurnos = 0;
            let dayNocturnos = 0;
            let dayTotalNeto = 0;
            const dayWorkersList: any[] = [];

            for (const r of records) {
              const turno = r.dt5 || 'D';
              const monto = Number(r.dt6) || 0;
              const extra = Number(r.dt7) || 0;
              const descuento = Number(r.dt8) || 0;
              const neto = monto + extra;
              dayTotalNeto += neto;

              if (turno === 'N') {
                dayNocturnos++;
                nocturnosTotal++;
              } else {
                dayDiurnos++;
                diurnosTotal++;
              }

              // Acumular estadísticas del colaborador
              const workerKey = r.dt12 || 'Colaborador';
              if (!workersMap.has(workerKey)) {
                workersMap.set(workerKey, {
                  nombre: workerKey,
                  diurnos: 0,
                  nocturnos: 0,
                  totalNeto: 0,
                  totalDays: 0
                });
              }

              const wData = workersMap.get(workerKey)!;
              wData.totalNeto += neto;
              wData.totalDays += 1;
              if (turno === 'N') {
                wData.nocturnos += 1;
              } else {
                wData.diurnos += 1;
              }

              // Guardar la persona y su turno de este día específico
              dayWorkersList.push({
                nombre: workerKey,
                turno: turno === 'N' ? 'Nocturno 🌙' : 'Diurno ☀️'
              });
            }

            // Formatear la fecha
            const parts = dateStr.split('-');
            let dateLabel = dateStr;
            if (parts.length === 3) {
              const dObj = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
              const opts: Intl.DateTimeFormatOptions = { weekday: 'long', day: 'numeric', month: 'short' };
              dateLabel = dObj.toLocaleDateString('es-ES', opts);
              dateLabel = dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1);
            }

            daysData.push({
              dateStr: dateStr,
              label: dateLabel,
              totalNeto: dayTotalNeto,
              diurnos: dayDiurnos,
              nocturnos: dayNocturnos,
              totalWorkers: records.length,
              workers: dayWorkersList
            });
          }
        }
      }
    } catch (e) {
      console.error('Error fetching advanced payroll stats', e);
    } finally {
      await this.metodosUi.hideLoading();
    }

    const sections: InfoSection[] = [
      {
        title: 'Información General',
        items: [
          { label: 'Cliente', value: this.trabajo?.cliente || 'N/A', icon: 'business-outline' },
          { label: 'Dirección', value: this.trabajo?.lugar || 'N/A', icon: 'location-outline' },
          { label: 'Estado', value: this.trabajo?.estado || 'N/A', icon: 'flag-outline' },
          { label: 'Solicitante', value: this.trabajo?.solicitante || 'N/A', icon: 'person-outline' },
          { label: 'Incluye Materiales', value: this.trabajo?.incluye_material === 'Y' ? 'Sí' : 'No', icon: 'construct-outline', iconColor: this.trabajo?.incluye_material === 'Y' ? '#27ae60' : '#7f8c8d' },
          { label: 'Fecha Creación', value: this.datePipe.transform(this.trabajo?.fecha_creacion, 'mediumDate') || 'N/A', icon: 'time-outline' }
        ]
      }
    ];

    // Insertar descripción como acordeón justo después de Información General
    if (this.trabajo?.descripcion) {
      sections.push({
        title: 'Descripción del Trabajo',
        items: [{
          label: 'Detalle',
          value: this.trabajo.descripcion,
          icon: 'document-text-outline',
          rowClass: 'row-accordion-description',
          expanded: false
        }]
      });
    }

    // Insertar Resumen Financiero inmediatamente después de la descripción
    sections.push({
      title: 'Resumen Financiero',
      items: [
        { label: 'Cotización (Neto)', value: `$${(this.cotizacionInfo?.total ?? 0).toFixed(2)}`, icon: 'document-text-outline', valueClass: 'positive' },
        { label: 'Planilla (Costos)', value: `-$${(this.planillaTotalAmount ?? 0).toFixed(2)}`, icon: 'people-outline', valueClass: 'negative' },
        { label: 'Gastos (Insumos)', value: `-$${(this.gastosTotalAmount ?? 0).toFixed(2)}`, icon: 'cash-outline', valueClass: 'negative' },
        { label: 'Subtotal Operativo', value: `-$${((this.planillaTotalAmount ?? 0) + (this.gastosTotalAmount ?? 0)).toFixed(2)}`, icon: 'calculator-outline', valueClass: 'negative', rowClass: 'row-subtotal', sublabel: 'Planilla + Insumos' },
        { label: 'Gastos (PPT)', value: `-$${(this.pptsAmount ?? 0).toFixed(2)}`, icon: 'briefcase-outline', valueClass: 'negative' },
        { label: 'Balance Final', value: `$${this.getBalance().toFixed(2)}`, icon: 'trending-up-outline', valueClass: this.getBalance() >= 0 ? 'positive emphasis' : 'negative emphasis' }
      ]
    });

    // Insertar Estadísticas del Trabajo inmediatamente después del Resumen Financiero
    const estadisticasItems: any[] = [
      { label: 'Días Trabajados', value: this.diasTrabajados, icon: 'calendar-outline' },
      { label: 'Fecha Inicio', value: this.datePipe.transform(this.primerDiaPlanilla, 'mediumDate') || 'N/A', icon: 'play-outline' }
    ];

    if (this.trabajo?.estado === 'Finalizado') {
      let latestDate: string | null = null;
      if (dates && dates.length > 0) {
        const sortedDates = [...dates].sort((a, b) => a.localeCompare(b));
        latestDate = sortedDates[sortedDates.length - 1];
      }
      estadisticasItems.push({
        label: 'Fecha Fin',
        value: this.datePipe.transform(latestDate, 'mediumDate') || 'N/A',
        icon: 'stop-outline'
      });
    }

    if (this.fechaFirmaArs) {
      let formattedFirmaArs = this.fechaFirmaArs;
      if (this.fechaFirmaArs.includes('/')) {
        const parts = this.fechaFirmaArs.split('/');
        if (parts.length === 3) {
          const isoDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
          formattedFirmaArs = this.datePipe.transform(isoDate, 'mediumDate') || this.fechaFirmaArs;
        }
      }
      estadisticasItems.push({
        label: 'Firma ARS',
        value: formattedFirmaArs,
        icon: 'checkmark-done-circle-outline',
        iconColor: '#27ae60'
      });
    }

    estadisticasItems.push({ label: 'Cotizaciones Vinculadas', value: this.cotizacionesCount, icon: 'documents-outline' });

    sections.push({
      title: 'Estadísticas del Trabajo',
      items: estadisticasItems
    });

    // Inyectar resumen consolidado de turnos
    if (diurnosTotal > 0 || nocturnosTotal > 0) {
      sections.push({
        title: 'Distribución de Turnos',
        items: [
          {
            label: 'Resumen Turnos',
            value: '',
            rowClass: 'row-custom-shifts-card',
            extraData: {
              diurnos: diurnosTotal,
              nocturnos: nocturnosTotal
            }
          }
        ]
      });
    }

    // Inyectar desglose de días individuales
    if (daysData.length > 0) {
      daysData.sort((a, b) => a.dateStr.localeCompare(b.dateStr)); // Más viejos primero

      sections.push({
        title: 'Desglose por Día Trabajado',
        items: daysData.map(d => ({
          label: d.label,
          value: `$${d.totalNeto.toFixed(2)}`,
          rowClass: 'row-custom-day-card',
          extraData: {
            diurnos: d.diurnos,
            nocturnos: d.nocturnos,
            totalWorkers: d.totalWorkers
          }
        }))
      });
    }

    // Inyectar desglose de trabajadores consolidado al final
    const workersList = Array.from(workersMap.values());
    if (workersList.length > 0) {
      workersList.sort((a, b) => a.nombre.localeCompare(b.nombre));

      sections.push({
        title: 'Resumen por Colaborador',
        items: workersList.map(w => ({
          label: w.nombre,
          value: `$${w.totalNeto.toFixed(2)}`,
          rowClass: 'row-custom-worker-card',
          extraData: {
            diurnos: w.diurnos,
            nocturnos: w.nocturnos,
            totalDays: w.totalDays
          }
        }))
      });
    }

    const modal = await this.modalCtrl.create({
      component: InfomodalComponent,
      componentProps: {
        title: 'Información del Trabajo',
        sections: sections
      }
    });
    await modal.present();
  }

  async openReportes() {
    if (!this.trabajo) return;
    const modal = await this.modalCtrl.create({
      component: ArsManagementComponent,
      componentProps: {
        trabajo: this.trabajo,
        clienteNombre: this.trabajo.cliente,
        cotizacionId: this.cotizacionInfo?.id
      }
    });
    await modal.present();
    await modal.onWillDismiss();
    await this.checkArsActivation(); // Refresh status
  }

  async openSeguimiento() {
    if (!this.trabajo) return;
    const modal = await this.modalCtrl.create({
      component: SeguimientoComponent,
      componentProps: { trabajo: this.trabajo }
    });
    await modal.present();
  }

  async openEnviarArs() {
    if (!this.trabajo) return;
    const modal = await this.modalCtrl.create({
      component: EnviarcorreoComponent,
      componentProps: {
        tituloModal: `Enviar Reporte ARS - Trabajo #${this.trabajo.id}`,
        moduloOrigen: 'ARS',
        idReferencia: this.trabajo.id,
        dataSource: this.trabajo,
        cotizacionId: this.cotizacionInfo?.id ?? null,
        asuntoPrellenado: `Reporte ARS - Trabajo #${this.trabajo.id} - ${this.trabajo.cliente || ''}`
      },
      cssClass: 'fullscreen-modal'
    });
    await modal.present();
  }

  async openEnviarSolicitud() {
    if (!this.trabajo) return;
    const modal = await this.modalCtrl.create({
      component: EnviarcorreoComponent,
      componentProps: {
        tituloModal: `Enviar Solicitud - Trabajo #${this.trabajo.id}`,
        moduloOrigen: 'SOLICITUD',
        idReferencia: this.trabajo.id,
        dataSource: this.trabajo,
        cotizacionId: this.cotizacionInfo?.id ?? null
      },
      cssClass: 'fullscreen-modal'
    });
    await modal.present();
  }

  getBalance(): number {
    const cotTotal = this.cotizacionInfo?.total ?? 0;
    const planilla = this.planillaTotalAmount ?? 0;
    const gastos = this.gastosTotalAmount ?? 0;
    const ppts = this.pptsAmount ?? 0;
    return cotTotal - planilla - gastos - ppts;
  }

  close() {
    this.modalCtrl.dismiss();
  }

  async openCotizacion() {
    if (!this.cotizacionInfo && !this.loadingCotizacion) {
      await this.addCotizacionFromMenu();
      return;
    }

    const modal = await this.modalCtrl.create({
      component: CotizacioneslistComponent,
      componentProps: { trabajo: this.trabajo }
    });
    await modal.present();
    await modal.onWillDismiss();
    await Promise.all([this.loadCotizacion(), this.loadMenuTotals()]);
  }

  async addCotizacionFromMenu() {
    const buttons: any[] = [
      {
        text: 'Crear nueva cotización',
        icon: 'add-circle-outline',
        handler: () => {
          this.createNewCotizacion();
        }
      },
      {
        text: 'Vincular cotización existente',
        icon: 'link-outline',
        handler: () => {
          this.linkExistingCotizacion();
        }
      }
    ];

    if (this.cotizacionInfo) {
      buttons.unshift({
        text: 'Desvincular cotización',
        icon: 'unlink-outline',
        cssClass: 'action-sheet-danger',
        handler: () => {
          this.unlinkCotizacion();
        }
      });
    }

    buttons.push({
      text: 'Cancelar',
      icon: 'close',
      role: 'cancel'
    });

    const actionSheet = await this.actionSheetCtrl.create({
      header: '¿Qué deseas hacer?',
      cssClass: 'custom-action-sheet',
      buttons: buttons
    });
    await actionSheet.present();
  }

  async createNewCotizacion() {
    const basePrice = (this.planillaTotalAmount || 0) + (this.gastosTotalAmount || 0);
    const modal = await this.modalCtrl.create({
      component: CotizacioneseditComponent,
      componentProps: {
        trabajo: this.trabajo,
        mode: 'add',
        basePrice: basePrice
      }
    });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    if (data?.action === 'saved') {
      await this.loadCotizacion();
      await this.loadMenuTotals();
    }
  }

  async unlinkCotizacion() {
    if (!this.cotizacionInfo) return;

    const confirm = await this.metodosUi.showConfirmation('Desvincular', `¿Estás seguro de desvincular la cotización #${this.cotizacionInfo.id} de este trabajo?`, 'Desvincular', true);
    if (confirm) {
      this.isUnlinking = true;
      try {
        const usuario = this.metodosGen.getUsuario();
        const payload = this.metodosGen.setModeloGeneral(
          'cotizaciones', 'unlinkCotizacionTrabajo',
          String(this.cotizacionInfo?.id),
          '', usuario, '', '', '', '', '', '', '', '', '', '', '', ''
        );
        await firstValueFrom(this.javapiService.getCommonModel(payload));
        
        await this.metodosUi.showToast('Cotización desvinculada correctamente', 'success');
        this.cotizacionInfo = null;
        await this.loadMenuTotals();
      } catch (e) {
        console.error('Error unlinking cotizacion:', e);
        await this.metodosUi.showToast('Error al desvincular cotización', 'danger');
      } finally {
        this.isUnlinking = false;
      }
    }
  }

  async linkExistingCotizacion() {
    const modal = await this.modalCtrl.create({
      component: CotizacioneslistComponent,
      componentProps: {
        trabajo: this.trabajo,
        selectionMode: true
      }
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    if (data?.linked) {
      await this.loadCotizacion();
      await this.loadMenuTotals();
    }
  }

  async openPlanilla() {
    const modal = await this.modalCtrl.create({
      component: PlanillaCalendarComponent,
      componentProps: { trabajo: this.trabajo }
    });
    await modal.present();
    await modal.onDidDismiss();
    await this.loadMenuTotals();
  }

  addPlanilla() { this.openPlanilla(); }

  async openGastos() {
    const modal = await this.modalCtrl.create({
      component: GastoseditComponent,
      componentProps: { trabajo: this.trabajo }
    });
    await modal.present();
    await modal.onDidDismiss();
    await this.loadMenuTotals();
  }

  addGastos() { this.openGastos(); }

  async openGalerias() {
    const modal = await this.modalCtrl.create({
      component: GaleriaTrabajosComponent,
      componentProps: { trabajo: this.trabajo }
    });
    await modal.present();
    // Refresh totals and status when closing gallery because of automatic status change
    await modal.onWillDismiss();
    await this.loadMenuTotals();
  }

  addGalerias() {
    this.openGalerias();
  }

}
